import React, { useEffect, useMemo, useState } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';

import { EAvailableSpeed } from '@r1-frontend/entities/ContactAddress/initialState';
import { EVerificationStatus } from '@r1-frontend/entities/ContactDetail/initialState';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { ButtonGroup } from '@r1-frontend/ui-react/components/buttons/buttonGroup/ButtonGroup';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import TextWrapper from '@r1-frontend/ui-react/components/typography/TextWrapper';

import { AddressCheckMessage } from '~/src/features/full-buy/AddressCheckMessage';
import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { GIGABIT_SPEED, MEGABIT_SPEED } from '~/src/features/full-buy/consts';
import ContactAddress from '~/src/features/full-buy/ContactAddress';
import { MainLayout } from '~/src/features/full-buy/containers/MainLayout/MainLayout';
import { setDisplayTariffLineState, setTariffFilters, setTariffLineAlias } from '~/src/features/full-buy/store/actions';
import { ETariffFilters } from '~/src/features/full-buy/store/initialState';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import { selectFilteredTariffs } from '~/src/features/full-buy/store/selectors/selectFilteredTariffs';
import { TState } from '~/src/store';

import { HasAgreementPopup } from './HasAgreementPopup';

type TAdditionalTariffs = 'only100' | 'less1000';

export const Address = (): JSX.Element => {
    const analytics = useAnalytics();

    const {
        selectedTariff,
        addressStatus,
        isEdit,
        verifiedAddressData: {
            isHouseConnected,
            isHouseCottage,
            availableSpeed,
        },
    } = useSelector((state: TState) => ({
        selectedTariff: selectCurrentTariff(state),
        filteredTariffs: selectFilteredTariffs(state),
        addressStatus: state.contactAddress.verifiedAddressData.status,
        isEdit: state.contactAddress.isEdit,

        verifiedAddressData: state.contactAddress.verifiedAddressData,
    }), shallowEqual);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [isEdit]);

    const dispatch = useDispatch();

    const [displayAdditionalTariffLine, setDisplayAdditionalTariffLine] = useState<boolean | TAdditionalTariffs>(false);

    const { title = '', speed: tariffSpeed = null } = selectedTariff || {};

    const tariffSpeedAcrossEnum = useMemo(() => {
        if (tariffSpeed === null) {
            return null;
        }

        if (tariffSpeed >= GIGABIT_SPEED) {
            return EAvailableSpeed.more1000;
        }

        if (tariffSpeed > MEGABIT_SPEED) {
            return EAvailableSpeed.between100and1000;
        }

        return EAvailableSpeed.lessOrEqual100;
    }, [tariffSpeed]);

    const shouldConnect = isHouseConnected && (
        (Number(tariffSpeedAcrossEnum) > EAvailableSpeed.lessOrEqual100 && Number(tariffSpeedAcrossEnum) <= availableSpeed) ||
        (Number(tariffSpeedAcrossEnum) === EAvailableSpeed.lessOrEqual100)
    );

    const addressIsVerified = addressStatus === EVerificationStatus.verified;

    useEffect(() => {
        if (addressStatus === EVerificationStatus.verified) {
            const allowedSpeed = availableSpeed === EAvailableSpeed.lessOrEqual100 ? 'only100' : 'less1000';
            if (!isHouseConnected) {
                analytics.address.verificationFailed('Ваш дом пока не подключён к Дом.ру');
                return;
            }

            if (Number(tariffSpeedAcrossEnum) > availableSpeed) {
                analytics.address.verificationFailed(`Ваш дом пока не подключен к такой скорости (${allowedSpeed})`);
                return;
            }

            analytics.address.verificationSuccess();
        }

        if (addressStatus === EVerificationStatus.error) {
            analytics.address.verificationFailed('Не смогли проверить ваш адрес');
        }
    }, [addressStatus]);

    useEffect(() => {
        if (!tariffSpeed || !addressIsVerified || !isHouseConnected) {
            return;
        }

        if (!shouldConnect) {
            setDisplayAdditionalTariffLine(availableSpeed === EAvailableSpeed.lessOrEqual100 ? 'only100' : 'less1000');
            batch(() => {
                dispatch(setTariffFilters([availableSpeed === EAvailableSpeed.lessOrEqual100 ? ETariffFilters.SPEED100 : ETariffFilters.SPEED_LESS_1000]));
                dispatch(setTariffLineAlias(''));
                dispatch(setDisplayTariffLineState(true));
            });
        }
    }, [dispatch, tariffSpeed, addressIsVerified, shouldConnect]);

    useEffect(() => {
        if (displayAdditionalTariffLine && isEdit) {
            setDisplayAdditionalTariffLine(false);
        }
    }, [displayAdditionalTariffLine, isEdit]);

    const goToSpeed100Tariffs = () => {
        if (availableSpeed === EAvailableSpeed.lessOrEqual100) {
            analytics.address.only100Clicked();
        } else {
            analytics.address.lessThan1000Clicked();
        }

        location.href = `/full-buy?filters=${availableSpeed === EAvailableSpeed.lessOrEqual100 ? ETariffFilters.SPEED100 : ETariffFilters.SPEED_LESS_1000}`;
    };

    return (
        <MainLayout>
            {isEdit &&
                <TextWrapper $lastIndent={false}>
                    <H3>{`Проверьте, доступен ли тариф «${title}» по вашему адресу`}</H3>
                    <HasAgreementPopup/>
                </TextWrapper>
            }

            {!isEdit &&
                <AddressCheckMessage
                    gigabitMayConnect={Number(tariffSpeedAcrossEnum) <= availableSpeed && availableSpeed > EAvailableSpeed.lessOrEqual100}
                    requiredGigaSpeed={Number(tariffSpeedAcrossEnum) > EAvailableSpeed.lessOrEqual100}
                    availableSpeed={availableSpeed}
                    isHouseConnected={isHouseConnected}
                    tariffName={title}
                    isNotFlatAddress={isHouseCottage}
                    addressStatus={addressStatus}
                />
            }
            {/* Альтернативные тарифы */}
            {displayAdditionalTariffLine &&
                <ButtonGroup>
                    <Button onClick={goToSpeed100Tariffs}>
                        {availableSpeed === EAvailableSpeed.lessOrEqual100
                            ? 'Все тарифы 100 Мбит/с'
                            : 'Все тарифы до 1000 Мбит/с'
                        }
                    </Button>
                </ButtonGroup>
            }
            {selectedTariff &&
                <ContactAddress
                    showConfirmButton={false}
                />
            }
        </MainLayout>
    );
};
